import { bytesToBase64, base64ToBytes } from "@rezprotocol/sdk/client";
import { Hash } from "@rezprotocol/sdk/hash";
import { DeviceFanoutCacheEntryV1 } from "../../records/domain/DeviceFanoutCacheEntryV1.js";

export const DEVICE_FANOUT_PREFIX = "app:devicefanout/";

// A retry of a failed per-device send must REPLAY the identical sealed bytes —
// re-encrypting advances the sender's send-ratchet a second time, duplicating
// delivery and burning the recipient's skipped-key tolerance (Audit R2 #4). The
// in-memory cache that enforced this was lost on sender restart, so a recovery
// re-send after a crash re-encrypted everything (Audit R3 #4). 72h matches the
// node's outbound-queue retention, so a legitimate retry never finds the entry
// expired; past that the message is terminally failed and a fresh seal is fine.
const DEFAULT_TTL_MS = 72 * 60 * 60 * 1000;

// A cache slot is identified by the full (owner, peer, message, device) tuple —
// not messageId::deviceId alone (Audit R4 #7) — so no two owners/peers/messages
// can collide. NUL-separated so a field boundary is never ambiguous.
function coordsValid(coords) {
  return Boolean(coords)
    && typeof coords.ownerAccountId === "string" && coords.ownerAccountId.length > 0
    && typeof coords.peerAccountId === "string" && coords.peerAccountId.length > 0
    && typeof coords.messageId === "string" && coords.messageId.length > 0
    && typeof coords.peerDeviceId === "string" && coords.peerDeviceId.length > 0;
}
function slotKey(coords) {
  const material = coords.ownerAccountId + "\u0000" + coords.peerAccountId
    + "\u0000" + coords.messageId + "\u0000" + coords.peerDeviceId;
  const h = Hash.sha256Hex(material);
  return DEVICE_FANOUT_PREFIX + h.slice(0, 43);
}

/**
 * DeviceFanoutCacheStore — durable per-(owner, peer, messageId, peerDeviceId)
 * sealed ciphertext, so a send retry (including one after a sender restart)
 * replays identical bytes instead of re-encrypting.
 *
 * The persisted value is a DeviceFanoutCacheEntryV1 RRecord (Audit R4 #7), not
 * an ad-hoc object: the sealed value from sdk.sealForPeerDevice
 * (`{ object: { payloadBytes (Uint8Array), metadata, capChain }, address }`)
 * is base64-encoded into the record's `payloadB64`; metadata / capChain /
 * address are JSON-plain.
 *
 * Entries carry `deliveredOk`: once a device is delivered (or the node queued
 * it durably), a later retry of the same messageId must skip that device rather
 * than re-dispatch (re-dispatching identical ratchet bytes double-advances the
 * recipient's receive ratchet). TTL is enforced AT LOOKUP (an aged entry reads
 * as a miss and is evicted), not only at capacity — and `prune()` sweeps the
 * KV so entries that are never read again cannot accumulate.
 */
export class DeviceFanoutCacheStore {
  #kv;
  #clock;
  #ttlMs;

  constructor({ storageProvider, clock = () => Date.now(), ttlMs = DEFAULT_TTL_MS } = {}) {
    if (!storageProvider || typeof storageProvider.getKeyValueStore !== "function") {
      throw new Error("DeviceFanoutCacheStore requires storageProvider.getKeyValueStore()");
    }
    this.#kv = storageProvider.getKeyValueStore(null);
    this.#clock = clock;
    this.#ttlMs = ttlMs;
  }

  #toSealed(entry) {
    return {
      object: {
        payloadBytes: base64ToBytes(entry.payloadB64),
        metadata: entry.metadata && typeof entry.metadata === "object" ? entry.metadata : {},
        capChain: Array.isArray(entry.capChain) && entry.capChain.length > 0 ? entry.capChain : null,
      },
      address: entry.address,
    };
  }

  /**
   * @param {{ ownerAccountId, peerAccountId, messageId, peerDeviceId }} coords
   * @returns {Promise<{ sealed: object, deliveredOk: boolean }|null>}
   */
  async get(coords) {
    if (!coordsValid(coords)) return null;
    const key = slotKey(coords);
    const raw = await Promise.resolve(this.#kv.get(key));
    if (!raw || typeof raw !== "object" || typeof raw.payloadB64 !== "string") return null;
    if (this.#clock() - Number(raw.createdAtMs || 0) > this.#ttlMs) {
      // TTL at lookup: an aged entry reads as a miss and is evicted in place.
      await Promise.resolve(this.#kv.delete(key));
      return null;
    }
    const entry = new DeviceFanoutCacheEntryV1(raw);
    return { sealed: this.#toSealed(entry), deliveredOk: entry.deliveredOk === true };
  }

  /**
   * Persist a freshly-sealed device ciphertext (deliveredOk = false).
   * @param {{ ownerAccountId, peerAccountId, messageId, peerDeviceId }} coords
   * @param {object} sealed - { object, address } from sealForPeerDevice
   */
  async put(coords, sealed) {
    if (!coordsValid(coords)) return;
    const object = sealed && sealed.object ? sealed.object : {};
    const entry = new DeviceFanoutCacheEntryV1({
      ownerAccountId: coords.ownerAccountId,
      peerAccountId: coords.peerAccountId,
      peerDeviceId: coords.peerDeviceId,
      messageId: coords.messageId,
      payloadB64: bytesToBase64(object.payloadBytes),
      metadata: object.metadata && typeof object.metadata === "object" ? object.metadata : null,
      capChain: Array.isArray(object.capChain) ? object.capChain : null,
      address: sealed && sealed.address ? sealed.address : null,
      deliveredOk: false,
      createdAtMs: this.#clock(),
    });
    await Promise.resolve(this.#kv.set(slotKey(coords), entry.toJSON()));
  }

  /**
   * Mark a device delivered so any future retry of this messageId skips it.
   * No-op if the entry is gone (already pruned/expired).
   * @param {{ ownerAccountId, peerAccountId, messageId, peerDeviceId }} coords
   */
  async markDelivered(coords) {
    if (!coordsValid(coords)) return;
    const key = slotKey(coords);
    const raw = await Promise.resolve(this.#kv.get(key));
    if (!raw || typeof raw !== "object" || typeof raw.payloadB64 !== "string") return;
    // Re-emit as a validated record so the persisted shape stays a record.
    const entry = new DeviceFanoutCacheEntryV1({ ...raw, deliveredOk: true });
    await Promise.resolve(this.#kv.set(key, entry.toJSON()));
  }

  /**
   * Sweep expired entries so ones that are never read again cannot accumulate.
   * Best-effort; called on service start.
   * @returns {Promise<number>} evicted count
   */
  async prune() {
    const keys = await Promise.resolve(this.#kv.keys(DEVICE_FANOUT_PREFIX));
    if (!Array.isArray(keys)) return 0;
    const now = this.#clock();
    let evicted = 0;
    for (const key of keys) {
      const raw = await Promise.resolve(this.#kv.get(key));
      if (!raw || typeof raw !== "object" || now - Number(raw.createdAtMs || 0) > this.#ttlMs) {
        await Promise.resolve(this.#kv.delete(key));
        evicted += 1;
      }
    }
    return evicted;
  }
}
